// ── Types d'inscription supportés (URL slug) ───────────────────
export type UserType = 'client' | 'livreur' | 'livreur-pro';

/** Nom du type côté API Laravel (types.name) */
export type BackendTypeName =
    | 'client'
    | 'community_delivery'
    | 'professional_delivery';

export type StepId =
    | 'contact'
    | 'identity'
    | 'vehicle'
    | 'availability'
    | 'documents'
    | 'consents';

export interface StepConfig {
    id: StepId;
    label: string;
}

export interface UserTypeConfig {
    backendTypeName: BackendTypeName;
    jwtRoles: string[];
    label: string;
    icon: string;
    color: string;
    description: string;
    dashboardPath: string;
    steps: StepConfig[];
    emailRequired: boolean;
}

export const USER_TYPE_CONFIG: Record<UserType, UserTypeConfig> = {
    client: {
        backendTypeName: 'client',
        jwtRoles: ['client'],
        label: 'Client',
        icon: 'ri-user-line',
        color: 'teal',
        description: 'Profitez de livraisons économiques partout',
        dashboardPath: '/dashboard/client',
        emailRequired: false,
        steps: [
            { id: 'contact', label: 'Informations' },
            { id: 'identity', label: 'Zone' },
            { id: 'consents', label: 'Finalisation' },
        ],
    },
    livreur: {
        backendTypeName: 'community_delivery',
        jwtRoles: ['community_delivery'],
        label: 'Livreur Communautaire',
        icon: 'ri-community-line',
        color: 'orange',
        description: 'Livraisons flexibles dans votre quartier',
        dashboardPath: '/dashboard/livreur',
        emailRequired: true,
        steps: [
            { id: 'contact', label: 'Inscription' },
            { id: 'identity', label: 'Zone' },
            { id: 'vehicle', label: 'Véhicule' },
            { id: 'availability', label: 'Disponibilité' },
            { id: 'documents', label: 'Documents' },
            { id: 'consents', label: 'Validation' },
        ],
    },
    'livreur-pro': {
        backendTypeName: 'professional_delivery',
        jwtRoles: ['professional_delivery'],
        label: 'Livreur Professionnel',
        icon: 'ri-briefcase-line',
        color: 'blue',
        description: 'Carrière professionnelle avec validation de dossier',
        dashboardPath: '/dashboard/livreur-pro',
        emailRequired: true,
        steps: [
            { id: 'contact', label: 'Inscription' },
            { id: 'identity', label: 'Zone' },
            { id: 'vehicle', label: 'Véhicule' },
            { id: 'availability', label: 'Disponibilité' },
            { id: 'documents', label: 'Documents' },
            { id: 'consents', label: 'Validation' },
        ],
    },
};

export const REGISTERABLE_USER_TYPES: UserType[] = ['client', 'livreur', 'livreur-pro'];

export const INTERNAL_ROLES = [
    { id: 'client', label: 'Membre', icon: 'ri-user-line' },
    { id: 'community_delivery', label: 'Livreur', icon: 'ri-truck-line' },
    { id: 'admin', label: 'Administrateur', icon: 'ri-shield-line' },
];

export const ROLE_DASHBOARD_MAP: Record<string, string> = {
    client: '/dashboard/client',
    community_delivery: '/dashboard/livreur',
    professional_delivery: '/dashboard/livreur-pro',
    relay_point: '/dashboard/point-relais',
    company: '/dashboard/company',
    admin: '/dashboard/admin',
    super_admin: '/dashboard/super-admin',
};

export interface RegisterReference {
    types: { id: number; name: string }[];
    zones: { id: number; name: string }[];
    vehicle_types: { id: number; name: string; default_max_weight?: number | string | null; default_max_volume?: number | string | null }[];
    specializations: { id: number; name: string }[];
    availability_slots: {
        id: number;
        name: string;
        is_week_based: boolean;
        start_time: string | null;
        end_time: string | null;
    }[];
    categories: { id: number; name: string; type?: string }[];
}

export interface FormState {
    firstName: string;
    lastName: string;
    phone: string;
    phoneCountryCode: string;
    email: string;
    emailVerificationToken: string;
    password: string;
    confirmPassword: string;
    zoneId: string;
    countryId: number | null;
    regionId: number | null;
    prefectureId: number | null;
    communeId: number | null;
    districtId: number | null;
    secteurId: number | null;
    coverageSecteurIds: number[];
    coverageSecteurLabels: Array<{ id: number; label: string }>;
    vehicleTypeId: string;
    capacityWeightKg: string;
    capacityVolumeM3: string;
    specializationIds: number[];
    availabilitySlotIds: number[];
    coverageZoneIds: number[];
    documents: Record<string, File | null>;
    acceptTerms: boolean;
    acceptPrivacy: boolean;
    acceptQuality: boolean;
    acceptVerification: boolean;
    acceptNotifications: boolean;
}

export const EMPTY_USER: FormState = {
    firstName: '',
    lastName: '',
    phone: '',
    phoneCountryCode: '+224',
    email: '',
    emailVerificationToken: '',
    password: '',
    confirmPassword: '',
    zoneId: '',
    countryId: null,
    regionId: null,
    prefectureId: null,
    communeId: null,
    districtId: null,
    secteurId: null,
    coverageSecteurIds: [],
    coverageSecteurLabels: [],
    vehicleTypeId: '',
    capacityWeightKg: '',
    capacityVolumeM3: '',
    specializationIds: [],
    availabilitySlotIds: [],
    coverageZoneIds: [],
    documents: {},
    acceptTerms: false,
    acceptPrivacy: false,
    acceptQuality: false,
    acceptVerification: false,
    acceptNotifications: false,
};

export function getConfig(userType: string): UserTypeConfig | null {
    return USER_TYPE_CONFIG[userType as UserType] ?? null;
}

export function isValidUserType(userType: string): userType is UserType {
    return userType in USER_TYPE_CONFIG;
}

export function isDriverUserType(userType: UserType): boolean {
    return userType === 'livreur' || userType === 'livreur-pro';
}

export function isRegisterReferenceReady(
    reference: RegisterReference,
    userType: UserType,
): boolean {
    const config = USER_TYPE_CONFIG[userType];
    const hasType = reference.types.some((type) => type.name === config.backendTypeName);

    if (!hasType) {
        return false;
    }

    if (!isDriverUserType(userType)) {
        return true;
    }

    return reference.vehicle_types.length > 0
        && reference.specializations.length > 0
        && reference.availability_slots.length > 0
        && reference.categories.length > 0;
}

export function resolveDashboard(roleCode: string): string {
    return ROLE_DASHBOARD_MAP[roleCode] ?? '/';
}

export function buildRegisterPayload(
    form: FormState,
    userType: UserType,
    reference: RegisterReference,
): Record<string, unknown> {
    const config = USER_TYPE_CONFIG[userType];
    const phone = `${form.phoneCountryCode}${form.phone.replace(/\D/g, '')}`;

    const type = reference.types.find(t => t.name === config.backendTypeName);
    if (!type) {
        throw new Error(`Type d'inscription introuvable : ${config.backendTypeName}`);
    }

    if (!form.secteurId && !form.zoneId) {
        throw new Error('La localisation (secteur) est obligatoire.');
    }

    const payload: Record<string, unknown> = {
        firstnames: form.firstName.trim(),
        lastname: form.lastName.trim(),
        phone,
        password: form.password,
        password_confirmation: form.confirmPassword,
        type_id: type.id,
        secteur_id: form.secteurId ?? undefined,
    };

    if (form.zoneId) {
        payload.zone_id = Number(form.zoneId);
    } else if (reference.zones[0]?.id) {
        payload.zone_id = reference.zones[0].id;
    }

    if (form.email.trim()) {
        payload.email = form.email.trim();
        payload.email_verification_token = form.emailVerificationToken;
    }

    if (isDriverUserType(userType)) {
        payload.vehicle_capacity = {
            vehicle_type_id: Number(form.vehicleTypeId),
            max_weight: Number(form.capacityWeightKg),
            max_volume: Number(form.capacityVolumeM3),
        };
        payload.specialization_ids = form.specializationIds;
        payload.availability_slot_ids = form.availabilitySlotIds;
        payload.secteur_ids = form.coverageSecteurIds.length > 0
            ? form.coverageSecteurIds
            : (form.secteurId ? [form.secteurId] : []);

        const documents = buildDocumentsPayload(form);
        if (documents.length > 0) {
            payload.documents = documents;
        }
    }

    return payload;
}

function buildDocumentsPayload(form: FormState): Array<Record<string, unknown>> {
    const startedAt = new Date().toISOString().slice(0, 10);
    const endedAt = new Date(Date.now() + 730 * 86400000).toISOString().slice(0, 10);
    const result: Array<Record<string, unknown>> = [];

    Object.entries(form.documents).forEach(([key, file]) => {
        if (!file) return;

        const categoryId = Number(key);
        if (!categoryId) return;

        result.push({
            category_id: categoryId,
            file: file,
            started_at: startedAt,
            ended_at: endedAt,
        });
    });

    return result;
}
